import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateContractTemplate,
  useUpdateContractTemplate,
  useGetContractTemplate,
  useListClauses,
  useGetClause,
  getListContractTemplatesQueryKey,
  getGetContractTemplateQueryKey,
  getGetClauseQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { ArrowLeft, Layers, DollarSign, BookOpen, Plus, X, GripVertical, Loader2 } from "lucide-react";
import { Link } from "wouter";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableClauseRow({
  item,
  index,
  clauses,
  onRemove,
}: {
  item: ClauseRef;
  index: number;
  clauses: { id: number; title: string; category?: string }[];
  onRemove: (clauseId: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(item.clauseId),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  const clause = clauses.find((c) => c.id === item.clauseId);
  const title = item.clauseTitle ?? clause?.title ?? `Clause #${item.clauseId}`;
  const category = clause?.category;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2.5 select-none"
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground touch-none"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-xs font-medium text-muted-foreground w-4 text-center">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-muted-foreground">
          {category && <span className="capitalize">{category.replace(/_/g, " ")}</span>}
          {item.clauseVersionNumber != null && (
            <span className="ml-1 text-muted-foreground/60">· v{item.clauseVersionNumber}</span>
          )}
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-destructive hover:text-destructive"
        onClick={() => onRemove(item.clauseId)}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(500),
  role: z.string().min(1, "Role is required"),
  // Tags this template as either a Contract or Term Sheet template. The
  // contract creation form filters templates by kind so users only see
  // applicable starting points.
  documentKind: z.enum(["contract", "term_sheet"]).default("contract"),
  sport: z.string().optional(),
  institutionType: z.string().optional(),
  annualSalary: z.coerce.number().min(0).optional(),
  buyoutClause: z.coerce.number().min(0).optional(),
  terminationConditions: z.string().optional(),
  notes: z.string().optional(),
});

type ClauseRef = {
  clauseId: number;
  clauseVersionId: number;
  position: number;
  groupLabel?: string | null;
  clauseTitle?: string;
  clauseVersionNumber?: number;
};

interface TemplatesEditorProps {
  mode: "new" | "edit";
}

export default function TemplatesEditor({ mode }: TemplatesEditorProps) {
  const [, setLocation] = useLocation();
  const params = useParams<{ id?: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { org } = useOrg();
  const templateId = params.id ? parseInt(params.id, 10) : 0;

  const { data: existing, isLoading: loadingExisting } = useGetContractTemplate(templateId, {
    query: {
      enabled: mode === "edit" && templateId > 0,
      queryKey: getGetContractTemplateQueryKey(templateId),
    },
  });

  const { data: clauses = [] } = useListClauses(undefined, {
    query: { enabled: !!org, queryKey: ["listClauses"] },
  });

  const [clauseRefs, setClauseRefs] = useState<ClauseRef[]>([]);
  const [selectedClauseId, setSelectedClauseId] = useState<string>("");
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");

  const { data: selectedClauseDetail } = useGetClause(
    selectedClauseId ? parseInt(selectedClauseId, 10) : 0,
    {
      query: {
        enabled: !!selectedClauseId,
        queryKey: getGetClauseQueryKey(selectedClauseId ? parseInt(selectedClauseId, 10) : 0),
      },
    },
  );

  const createTemplate = useCreateContractTemplate();
  const updateTemplate = useUpdateContractTemplate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      role: "head_coach",
      documentKind: "contract",
      sport: "",
      institutionType: "any",
      annualSalary: undefined,
      buyoutClause: undefined,
      terminationConditions: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (existing) {
      const df = existing.defaultFields ?? {};
      form.reset({
        name: existing.name,
        role: existing.role,
        documentKind: ((existing as { documentKind?: "contract" | "term_sheet" }).documentKind) ?? "contract",
        sport: existing.sport ?? "",
        institutionType: existing.institutionType ?? "any",
        annualSalary: (df as Record<string, unknown>).annualSalary != null ? Number((df as Record<string, unknown>).annualSalary) : undefined,
        buyoutClause: (df as Record<string, unknown>).buyoutClause != null ? Number((df as Record<string, unknown>).buyoutClause) : undefined,
        terminationConditions: ((df as Record<string, unknown>).terminationConditions as string | undefined) ?? "",
        notes: ((df as Record<string, unknown>).notes as string | undefined) ?? "",
      });

      if (existing.clauseRefs && existing.clauseRefs.length > 0) {
        const refs: ClauseRef[] = (existing.clauseRefs as ClauseRef[]).map((r) => ({ ...r }));
        setClauseRefs(refs);
      }
    }
  }, [existing, form]);

  useEffect(() => {
    if (org?.role !== "admin") {
      toast({ title: "Access denied", description: "Only admins can manage templates.", variant: "destructive" });
      setLocation("/templates");
    }
  }, [org, setLocation, toast]);

  const handleAddClause = () => {
    if (!selectedClauseId) return;
    const clauseId = parseInt(selectedClauseId, 10);
    const clause = clauses.find((c) => c.id === clauseId);
    if (!clause) return;

    if (clauseRefs.some((r) => r.clauseId === clauseId)) {
      toast({ title: "Already added", description: "This clause is already in the template.", variant: "destructive" });
      return;
    }

    const versions = selectedClauseDetail?.versions ?? [];
    const resolvedVersionId = selectedVersionId
      ? parseInt(selectedVersionId, 10)
      : (clause.latestVersion?.id ?? (versions[0]?.id ?? null));

    if (!resolvedVersionId) {
      toast({ title: "No version", description: "This clause has no published version yet.", variant: "destructive" });
      return;
    }

    const selectedVersion = versions.find((v) => v.id === resolvedVersionId) ?? clause.latestVersion;

    const newRef: ClauseRef = {
      clauseId,
      clauseVersionId: resolvedVersionId,
      position: clauseRefs.length,
      groupLabel: null,
      clauseTitle: clause.title,
      clauseVersionNumber: selectedVersion?.versionNumber,
    };
    setClauseRefs([...clauseRefs, newRef]);
    setSelectedClauseId("");
    setSelectedVersionId("");
  };

  const handleRemoveClause = (clauseId: number) => {
    setClauseRefs(clauseRefs.filter((r) => r.clauseId !== clauseId).map((r, i) => ({ ...r, position: i })));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = clauseRefs.findIndex((r) => String(r.clauseId) === active.id);
    const newIndex = clauseRefs.findIndex((r) => String(r.clauseId) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setClauseRefs(arrayMove(clauseRefs, oldIndex, newIndex).map((r, i) => ({ ...r, position: i })));
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const defaultFields = {
      ...(values.annualSalary != null && values.annualSalary > 0 ? { annualSalary: values.annualSalary } : {}),
      ...(values.buyoutClause != null && values.buyoutClause > 0 ? { buyoutClause: values.buyoutClause } : {}),
      ...(values.terminationConditions ? { terminationConditions: values.terminationConditions } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
    };

    const payload = {
      name: values.name,
      role: values.role,
      documentKind: values.documentKind,
      sport: values.sport || null,
      institutionType: (values.institutionType && values.institutionType !== "any") ? values.institutionType : null,
      defaultFields,
      clauseRefs: clauseRefs.map(({ clauseTitle: _t, ...r }) => r),
    };

    if (mode === "new") {
      createTemplate.mutate({ data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractTemplatesQueryKey() });
          toast({ title: "Template created", description: `"${values.name}" is ready to use.` });
          setLocation("/templates");
        },
        onError: (err: unknown) => handleApiError(toast, err, { fallbackTitle: "Could not create template" }),
      });
    } else {
      updateTemplate.mutate({ id: templateId, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractTemplatesQueryKey() });
          queryClient.invalidateQueries({ queryKey: getGetContractTemplateQueryKey(templateId) });
          toast({ title: "Template updated", description: `"${values.name}" has been saved.` });
          setLocation("/templates");
        },
        onError: (err: unknown) => handleApiError(toast, err, { fallbackTitle: "Could not update template" }),
      });
    }
  };

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  if (mode === "edit" && loadingExisting) {
    return (
      <div className="max-w-3xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const addableClausesList = clauses.filter((c) => !clauseRefs.some((r) => r.clauseId === c.id));

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/templates"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {mode === "new" ? "New Template" : "Edit Template"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {mode === "new" ? "Define default field values and clause set for this role." : "Update this template's default values and clauses."}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-muted-foreground" />
                Template Identity
              </CardTitle>
              <CardDescription>Name, role, sport, and institution type for this template.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name *</FormLabel>
                    <FormControl><Input placeholder="e.g. Head Football Coach — Public D1" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Coach Role *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="head_coach">Head Coach</SelectItem>
                          <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                          <SelectItem value="coordinator">Coordinator</SelectItem>
                          <SelectItem value="director_of_ops">Director of Operations</SelectItem>
                          <SelectItem value="olympic_sport_head_coach">Olympic Sport Head Coach</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sport"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sport</FormLabel>
                      <FormControl><Input placeholder="e.g. Football (leave blank for any)" {...field} /></FormControl>
                      <FormDescription>Leave blank to apply to all sports.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="documentKind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-template-document-kind"><SelectValue /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="term_sheet">Term Sheet</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>Term-sheet templates pre-fill the abbreviated /term-sheets/new form instead of the full contract form.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="institutionType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Institution Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? "any"}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Any institution type" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="any">Any institution type</SelectItem>
                        <SelectItem value="public_d1">Public D1</SelectItem>
                        <SelectItem value="public_d2">Public D2</SelectItem>
                        <SelectItem value="public_d3">Public D3</SelectItem>
                        <SelectItem value="private_d1">Private D1</SelectItem>
                        <SelectItem value="private_d2">Private D2</SelectItem>
                        <SelectItem value="private_d3">Private D3</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                Default Field Values
              </CardTitle>
              <CardDescription>
                These values pre-fill the contract form when this template is selected. All can be adjusted per contract.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="annualSalary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Base Salary ($)</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                          <Input type="number" className="pl-7" placeholder="0" {...field} value={field.value ?? ""} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="buyoutClause"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Buyout Clause ($)</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                          <Input type="number" className="pl-7" placeholder="0" {...field} value={field.value ?? ""} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="terminationConditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Termination Conditions</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder="Standard termination language for this role…"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder="Internal notes pre-filled on contracts from this template…"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
                Clause Library
              </CardTitle>
              <CardDescription>
                Attach clauses that will automatically be added to contracts created from this template.
                Drag to reorder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={selectedClauseId} onValueChange={(v) => { setSelectedClauseId(v); setSelectedVersionId(""); }}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={addableClausesList.length === 0 ? "All clauses added" : "Select a clause to add…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {addableClausesList.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.title}
                        {c.category && (
                          <span className="ml-2 text-xs text-muted-foreground capitalize">({c.category.replace(/_/g, " ")})</span>
                        )}
                        {c.versionCount != null && c.versionCount > 1 && (
                          <span className="ml-2 text-xs text-muted-foreground">{c.versionCount}v</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClauseId && selectedClauseDetail && (selectedClauseDetail.versions?.length ?? 0) > 1 && (
                  <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
                    <SelectTrigger className="w-full sm:w-[160px]">
                      <SelectValue placeholder="Latest version" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedClauseDetail.versions.map((v) => (
                        <SelectItem key={v.id} value={String(v.id)}>
                          v{v.versionNumber}
                          {v.id === selectedClauseDetail.latestVersion?.id ? " (latest)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddClause}
                  disabled={!selectedClauseId}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>

              {clauseRefs.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No clauses attached yet. Select from your clause library above.
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={clauseRefs.map((r) => String(r.clauseId))}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {clauseRefs.map((ref, index) => (
                        <SortableClauseRow
                          key={ref.clauseId}
                          item={ref}
                          index={index}
                          clauses={clauses}
                          onRemove={handleRemoveClause}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" asChild>
              <Link href="/templates">Cancel</Link>
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{mode === "new" ? "Creating…" : "Saving…"}</>
              ) : (
                mode === "new" ? "Create Template" : "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
